import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, Plus } from 'lucide-react';
import { api } from '../services/api';
import { theme, colorForCategory } from '../theme';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { readOdcLogo } from '../utils/odcLogoStorage';
import { downloadOdcBusinessReport, parseReportData } from '../utils/odcReportDownload';
import { useCommandPalette } from '../context/CommandPaletteContext';

type Stats = {
  agents_active: number;
  agents_cap: number;
  leads_today: number;
  leads_total: number;
  calls_today: number;
  posts_published_today: number;
  posts_scheduled: number;
  social_accounts_connected: number;
  subscription_status: string | null;
  subscription_plan_name: string | null;
  days_remaining: number | null;
};

type AgentRow = {
  id: string;
  agent_number: number;
  name: string;
  category: string;
  description: string | null;
  icon: string | null;
  is_enabled: boolean;
  status: string;
  toggle_locked?: boolean;
  label_status?: string | null;
};

type CalPost = {
  id: string;
  title: string;
  platform: string | null;
  scheduled_at: string | null;
  status: string;
};

type SocialRow = {
  platform: string;
  account_name: string | null;
  is_connected: boolean;
  followers_count: number;
};

type DashboardApiStats = {
  leads?: { today?: number; total?: number; thisWeek?: number; lastWeek?: number; trend?: number };
  calls?: { today?: number; total?: number };
  posts?: { today?: number; total?: number; scheduled?: number };
  agents?: { active?: number; idle?: number; paused?: number };
  leadsChart?: { date: string; count: number }[];
  leadSources?: { source: string; count: number }[];
};

const CATEGORIES = [
  'All',
  'Core AI Intelligence',
  'Omnipresence',
  'Content & Social',
  'Paid Ads & SEO',
  'Engagement & Network',
  'Technical SEO',
  'GEO & AI Visibility',
  'Game Changers',
  'Revenue Multipliers',
  'Trust & Credibility',
  'Operations & Control',
] as const;

const PLATFORM_ORDER = ['facebook', 'instagram', 'youtube', 'linkedin', 'whatsapp'] as const;

function platformIcon(p: string): string {
  const m: Record<string, string> = {
    facebook: '📘',
    instagram: '📸',
    youtube: '▶️',
    linkedin: '💼',
    whatsapp: '💬',
    twitter: '🐦',
  };
  return m[p.toLowerCase()] || '📱';
}

function mergeSocial(rows: SocialRow[]): SocialRow[] {
  const map = new Map(rows.map((r) => [r.platform.toLowerCase(), r]));
  return PLATFORM_ORDER.map((p) => {
    const x = map.get(p);
    return (
      x ?? {
        platform: p,
        account_name: null,
        is_connected: false,
        followers_count: 0,
      }
    );
  });
}

function groupCalendarByDay(posts: CalPost[]): { day: string; items: CalPost[] }[] {
  const map = new Map<string, CalPost[]>();
  for (const p of posts) {
    const key = p.scheduled_at
      ? new Date(p.scheduled_at).toLocaleDateString('en-IN', {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        })
      : 'Unscheduled';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(p);
  }
  return Array.from(map.entries()).map(([day, items]) => ({ day, items }));
}

function activityDotColor(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('broadcast') || t.includes('whatsapp')) return theme.success;
  if (t.includes('insight') || t.includes('instagram')) return theme.secondary;
  if (t.includes('lead')) return theme.primary;
  return theme.accent;
}

function timeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function DashboardPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { openPalette } = useCommandPalette();
  const { user, logout } = useAuth();
  const { theme: colorMode, toggle: toggleTheme } = useTheme();
  const [bizId, setBizId] = useState<string | null>(null);
  const [bizName, setBizName] = useState('');
  const [stats, setStats] = useState<Stats | null>(null);
  const [dashStats, setDashStats] = useState<DashboardApiStats | null>(null);
  const [analytics, setAnalytics] = useState<any>(null);
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [catTab, setCatTab] = useState<string>('All');
  const [activity, setActivity] = useState<any[]>([]);
  const [calendar, setCalendar] = useState<CalPost[]>([]);
  const [social, setSocial] = useState<SocialRow[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const activityRef = useRef<HTMLUListElement>(null);
  const [reportRow, setReportRow] = useState<Record<string, unknown> | null | undefined>(undefined);
  const [reportToast, setReportToast] = useState<string | null>(null);
  const reportToastHandled = useRef(false);

  const loadCore = useCallback(async () => {
    const { data: b } = await api.get<{
      business: {
        id: string;
        business_name: string;
        onboarding_step?: number;
        onboarding_complete?: boolean;
      } | null;
    }>('/business/me');
    if (!b.business) {
      setLoading(false);
      return null;
    }
    setBizId(b.business.id);
    setBizName(b.business.business_name);
    setOnboardingStep(Math.max(1, Math.min(6, b.business.onboarding_step ?? 1)));
    setOnboardingComplete(!!b.business.onboarding_complete);

    const [ag, cal, soc] = await Promise.all([
      api.get<{ agents: AgentRow[] }>(`/agents/business/${b.business.id}`),
      api.get<{ posts: CalPost[] }>('/dashboard/calendar'),
      api.get<{ social_accounts: SocialRow[] }>('/dashboard/social'),
    ]);
    setAgents(ag.data.agents);
    setCalendar(cal.data.posts);
    setSocial(mergeSocial(soc.data.social_accounts));
    setLoading(false);
    return b.business.id;
  }, []);

  useEffect(() => {
    void loadCore().catch(() => setLoading(false));
  }, [loadCore]);

  useEffect(() => {
    if (reportToastHandled.current) return;
    const st = (loc.state as { onboardingReportToast?: string } | null)?.onboardingReportToast;
    if (typeof st === 'string' && st.trim()) {
      reportToastHandled.current = true;
      setReportToast(st);
      nav(loc.pathname, { replace: true, state: {} });
    }
  }, [loc.pathname, loc.state, nav]);

  useEffect(() => {
    void api
      .get<{ report: Record<string, unknown> | null }>('/reports/latest')
      .then((r) => setReportRow(r.data.report ?? null))
      .catch(() => setReportRow(null));
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setStatsLoading(true);
        const [statsRes, analyticsRes, activityRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/analytics'),
          api.get('/dashboard/activity'),
        ]);
        setDashStats(statsRes.data);
        setStats(statsRes.data?.stats ?? null);
        setAnalytics(analyticsRes.data);
        setActivity(activityRes.data.activities || []);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setStatsLoading(false);
      }
    };
    void fetchDashboardData();
    const interval = setInterval(() => {
      void fetchDashboardData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    function close(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    const el = activityRef.current;
    if (!el) return;
    const id = window.setInterval(() => {
      el.scrollTop = Math.min(el.scrollTop + 1, el.scrollHeight - el.clientHeight);
      if (el.scrollTop >= el.scrollHeight - el.clientHeight - 2) el.scrollTop = 0;
    }, 80);
    return () => window.clearInterval(id);
  }, [activity.length]);

  async function toggleAgent(agentId: string, enabled: boolean) {
    if (!bizId) return;
    try {
      await api.post('/agents/toggle', { businessId: bizId, agentId, enabled });
    } catch {
      return;
    }
    const { data } = await api.get<{ agents: AgentRow[] }>(`/agents/business/${bizId}`);
    setAgents(data.agents);
    const { data: st } = await api.get('/dashboard/stats');
    setStats(st.stats ?? null);
    setDashStats(st ?? null);
  }

  const filteredAgents = useMemo(() => {
    return agents.filter((a) => catTab === 'All' || a.category === catTab);
  }, [agents, catTab]);

  const calByDay = useMemo(() => groupCalendarByDay(calendar), [calendar]);
  const headerLogoSrc = useMemo(() => (bizId ? readOdcLogo(bizId) : null), [bizId]);

  const onboardingMeta = useMemo(() => {
    const steps = [
      { key: 'profile', label: 'Profile' },
      { key: 'business', label: 'Business' },
      { key: 'social', label: 'Social Accounts' },
      { key: 'voice', label: 'Voice Clone' },
      { key: 'calendar', label: 'Content Calendar' },
      { key: 'money', label: 'Monetization' },
    ] as const;
    const done = steps.map((s, i) => {
      if (onboardingComplete) return true;
      if (s.key === 'profile') return true;
      if (s.key === 'business') return onboardingStep >= 2;
      if (s.key === 'social') return onboardingStep >= 4;
      if (s.key === 'voice') return onboardingStep >= 5;
      if (s.key === 'calendar') return onboardingStep >= 6;
      return false;
    });
    const moneyDone = onboardingComplete;
    const adjustedDone = done.map((d, i) => (i === 5 ? moneyDone : d));
    const n = adjustedDone.filter(Boolean).length;
    const pct = Math.round((n / steps.length) * 100);
    return { steps: steps.map((s, i) => ({ ...s, done: adjustedDone[i] })), pct, complete: onboardingComplete && n === steps.length };
  }, [onboardingStep, onboardingComplete]);

  const dateLabel = useMemo(
    () =>
      new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    []
  );

  if (loading) return <LoadingSpinner />;
  if (!bizId) {
    return (
      <div className="odc-page odc-card mx-auto max-w-lg p-10 text-center">
        <p style={{ color: 'var(--muted)' }}>No business yet.</p>
        <button type="button" className="odc-btn-primary mt-6" onClick={() => nav('/onboarding')}>
          Complete onboarding
        </button>
      </div>
    );
  }

  const cap = stats?.agents_cap;
  const agentsPct = cap != null && cap > 0 ? Math.min(100, Math.round(((dashStats?.agents?.active ?? 0) / cap) * 100)) : 0;
  const leadsTodayCount = dashStats?.leads?.today ?? 0;
  const leadsTrend = (dashStats?.leads?.total ?? 0) > 0 ? `${dashStats?.leads?.total ?? 0} total leads` : 'No leads today';
  const leadsTrendPct = dashStats?.leads?.trend ?? 0;
  const leadsTrendText = leadsTodayCount > 0
    ? `${leadsTrend} · ${leadsTrendPct >= 0 ? '+' : ''}${leadsTrendPct}%`
    : 'No leads today';
  const planLine = stats?.subscription_plan_name
    ? `${stats.subscription_plan_name}${stats.days_remaining != null ? ` · ${stats.days_remaining}d left` : ''}`
    : 'No active plan';

  const statBlocks = [
    {
      label: 'AI agents on',
      icon: '🤖',
      value: `${dashStats?.agents?.active ?? 0}`,
      color: theme.success,
      pct: agentsPct,
      trend: 'agents running',
      stagger: 1 as const,
    },
    {
      label: 'Leads today',
      icon: '👥',
      value: dashStats?.leads?.today ?? 0,
      color: '#3B82F6',
      pct: Math.min(100, ((dashStats?.leads?.today ?? 0) / 20) * 100),
      trend: leadsTrendText,
      stagger: 2 as const,
    },
    {
      label: 'AI calls',
      icon: '📞',
      value: dashStats?.calls?.today ?? 0,
      color: theme.secondary,
      pct: Math.min(100, ((dashStats?.calls?.today ?? 0) / 10) * 100),
      trend: (dashStats?.calls?.total ?? 0) > 0 ? `${dashStats?.calls?.total ?? 0} total calls` : 'AI dialer window',
      stagger: 3 as const,
    },
    {
      label: 'Posts today',
      icon: '📣',
      value: dashStats?.posts?.today ?? 0,
      color: theme.accent,
      pct: Math.min(100, ((dashStats?.posts?.today ?? 0) / 5) * 100),
      trend: `${dashStats?.posts?.scheduled ?? 0} scheduled`,
      stagger: 4 as const,
    },
    {
      label: 'AI Visibility',
      icon: '🔍',
      value: '—',
      color: '#06B6D4',
      pct: 0,
      trend: 'AI Search Optimized',
      stagger: 5 as const,
    },
  ];

  async function regenerateReport() {
    try {
      const { data } = await api.get<{ business: Record<string, unknown> | null }>('/business/me');
      const b = data.business;
      if (!b) return;
      const profile = {
        businessName: String(b.business_name || ''),
        businessType: String(b.business_type || 'Other'),
        location: String(b.location || b.city || ''),
        targetAudience: String(b.target_audience || ''),
        monthlyBudget: 'medium',
        primaryGoal: 'leads',
        socialPlatforms: ['Instagram', 'Facebook', 'WhatsApp', 'YouTube', 'LinkedIn'],
        teamSize: 'small',
        businessAge: 'established',
        description: String(b.description || ''),
      };
      await api.post('/reports/generate', profile);
      const latest = await api.get<{ report: Record<string, unknown> | null }>('/reports/latest');
      setReportRow(latest.data.report ?? null);
    } catch {
      /* ignore */
    }
  }

  function downloadSimpleFromRow(row: Record<string, unknown>) {
    const parsed = parseReportData(row.report_data);
    downloadOdcBusinessReport(parsed);
  }

  return (
    <div className="odc-page relative z-10 space-y-8">
      {reportToast ? (
        <div
          className="odc-card flex flex-wrap items-center justify-between gap-3 !rounded-xl !p-4"
          style={{ transform: 'none', borderColor: 'rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.10)' }}
          role="status"
        >
          <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
            {reportToast}
          </p>
          <button type="button" className="odc-btn-ghost !py-2 !text-xs" onClick={() => setReportToast(null)}>
            Dismiss
          </button>
        </div>
      ) : null}
      <header className="flex flex-wrap items-center gap-4">
        <div className="min-w-0 flex-1">
          <p
            className="td-section-eyebrow"
            style={{ color: 'var(--accent-2)' }}
          >
            {dateLabel}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {headerLogoSrc ? (
              <img
                src={headerLogoSrc}
                alt=""
                className="h-12 w-12 shrink-0 rounded-xl border object-contain odc-float"
                style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
              />
            ) : null}
            <h1 className="td-section-title" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
              Welcome back, <span className="odc-shimmer-text">{bizName}</span>!
            </h1>
          </div>
          <p className="mt-1 text-xs italic" style={{ color: 'var(--muted)' }}>
            Your AI agents are working around the clock. 🌙
          </p>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            Here is what your autonomous stack did recently.
          </p>
        </div>

        <div className="hidden flex-1 justify-center md:flex">
          <button
            type="button"
            className="odc-cmd-bar text-sm"
            style={{ color: 'var(--muted)' }}
            onClick={() => openPalette()}
          >
            <span className="rounded border px-1.5 py-0.5 text-[10px]" style={{ borderColor: 'var(--border)' }}>
              ⌘
            </span>
            <span>K</span>
            <span className="text-xs">Search anything…</span>
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <span className="odc-badge odc-badge-accent hidden sm:inline-flex">{planLine}</span>
          <button
            type="button"
            className="relative rounded-xl border p-2 transition"
            style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            aria-label="Notifications"
          >
            <Bell className="h-5 w-5" />
            <span className="odc-notif-dot">3</span>
          </button>
          <button type="button" className="odc-btn-primary" onClick={() => nav('/leads')}>
            <Plus className="h-4 w-4" />
            Add Lead
          </button>
          <button
            type="button"
            className="odc-theme-btn"
            aria-label={colorMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            onClick={toggleTheme}
          >
            {colorMode === 'dark' ? '☀️' : '🌙'}
          </button>
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              className="odc-btn-ghost !py-2"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
                style={{ background: theme.primary }}
              >
                {(user?.name || user?.mobile || '?').slice(0, 1).toUpperCase()}
              </span>
              <span className="hidden max-w-[100px] truncate sm:inline">{user?.name || 'Profile'}</span>
              <ChevronDown className="h-4 w-4 opacity-60" />
            </button>
            {menuOpen ? (
              <div
                className="odc-card absolute right-0 z-40 mt-2 w-44 overflow-hidden !rounded-xl !p-0 py-1 shadow-xl"
                style={{ boxShadow: '0 20px 50px rgba(0,0,0,0.35)' }}
              >
                <button
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm transition hover:bg-[var(--card2)]"
                  style={{ color: 'var(--text)' }}
                  onClick={() => {
                    setMenuOpen(false);
                    nav('/settings');
                  }}
                >
                  Settings
                </button>
                <button
                  type="button"
                  className="block w-full px-4 py-2 text-left text-sm transition hover:bg-[var(--card2)]"
                  style={{ color: theme.danger }}
                  onClick={() => {
                    logout();
                    window.location.href = '/login';
                  }}
                >
                  Log out
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      <section
        className="odc-card !p-4 sm:!p-5"
        style={{ transform: 'none', borderColor: 'rgba(16,185,129,0.28)' }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.primary }}>
              Onboarding
            </p>
            <p className="mt-1 text-sm font-semibold" style={{ color: 'var(--text)' }}>
              {onboardingMeta.pct}% complete
            </p>
          </div>
          {!onboardingMeta.complete ? (
            <button
              type="button"
              className="odc-btn-ghost !py-2 !text-xs font-bold"
              onClick={() => nav('/onboarding')}
            >
              Complete Setup →
            </button>
          ) : null}
        </div>
        <div className="odc-progress mt-3">
          <div
            className="odc-progress-fill"
            style={{
              width: `${onboardingMeta.pct}%`,
              background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
            }}
          />
        </div>
        <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-[11px]" style={{ color: 'var(--muted)' }}>
          {onboardingMeta.steps.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span style={{ color: s.done ? theme.primary : 'var(--muted)' }}>{s.done ? '✓' : '○'}</span>
              <span style={{ color: s.done ? 'var(--text)' : undefined }}>{s.label}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="odc-stat-cards grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {statsLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <div key={`stats-skeleton-${i}`} className="odc-stat-card animate-pulse" style={{ opacity: 0.7 }}>
                <div className="h-3 w-24 rounded bg-[var(--card2)]" />
                <div className="mt-3 h-8 w-20 rounded bg-[var(--card2)]" />
                <div className="mt-3 h-2 w-full rounded bg-[var(--card2)]" />
                <div className="mt-2 h-3 w-28 rounded bg-[var(--card2)]" />
              </div>
            ))
          : statBlocks.map((s) => (
              <div
                key={s.label}
                className={`odc-stat-card odc-stagger-${s.stagger}`}
                style={{ ['--stat-color' as string]: s.color }}
              >
                <div className="flex items-start justify-between gap-2">
                  <p
                    className="stat-label"
                    style={{ color: 'var(--text-secondary)', letterSpacing: '0.15em' }}
                  >
                    {s.label}
                  </p>
                  <span className="text-xl odc-float">{s.icon}</span>
                </div>
                <p className="stat-value mt-2" style={{ color: '#ffffff', fontSize: '1.7rem', fontWeight: 800, textShadow: `0 0 10px ${s.color}` }}>
                  {s.value}
                </p>
                <div className="odc-progress">
                  <div
                    className="odc-progress-fill"
                    style={{ width: `${s.pct}%`, background: `linear-gradient(90deg, ${s.color}, ${theme.secondary})` }}
                  />
                </div>
                <p className="mt-2 text-[11px]" style={{ color: 'var(--muted)' }}>
                  {s.trend}
                </p>
              </div>
            ))}
      </div>

      <section className="odc-card !p-5" style={{ transform: 'none' }}>
        <h3 className="section-title" style={{ color: 'var(--accent-2)' }}>
          Quick actions
        </h3>
        <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
          Jump into the workflows you use most — same theme on every surface.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            className="odc-btn-primary !py-2.5 !text-xs"
            onClick={() => nav('/leads')}
          >
            + Add Lead
          </button>
          <button
            type="button"
            className="odc-btn-ghost !py-2.5 !text-xs"
            onClick={() => nav('/content-generator')}
          >
            Generate Content
          </button>
          <button type="button" className="odc-btn-ghost !py-2.5 !text-xs" onClick={() => nav('/agent-ops')}>
            Run Agent
          </button>
          <button type="button" className="odc-btn-ghost !py-2.5 !text-xs" onClick={() => nav('/analytics')}>
            View Analytics
          </button>
        </div>
      </section>

      <section className="odc-card !p-5" style={{ transform: 'none' }}>
        <div className="grid gap-6 lg:grid-cols-2">
          <div>
            <h3 className="section-title" style={{ color: 'var(--accent-2)' }}>
              Leads Last 7 Days
            </h3>
            <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
              {analytics?.dailyLeads?.length ?? 0} tracked days from live analytics
            </p>
            <div className="mt-4 flex items-end gap-2">
              {(dashStats?.leadsChart ?? []).length === 0 ? (
                <div className="w-full rounded-xl border border-dashed p-8 text-center" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }}>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
                    No lead volume in this window yet
                  </p>
                  <p className="mt-2 text-xs">Capture enquiries from WhatsApp, ads, and walk-ins in Leads.</p>
                  <button type="button" className="odc-btn-primary mx-auto mt-4 !py-2 !text-xs" onClick={() => nav('/leads')}>
                    Open Leads CRM
                  </button>
                </div>
              ) : (
                (dashStats?.leadsChart ?? []).map((point) => {
                  const maxCount = Math.max(...(dashStats?.leadsChart?.map((x) => x.count) ?? [1]));
                  const barHeight = maxCount > 0 ? Math.max(8, (point.count / maxCount) * 100) : 8;
                  const dayLabel = new Date(point.date).toLocaleDateString('en-US', { weekday: 'short' });
                  return (
                    <div key={point.date} className="flex flex-1 flex-col items-center gap-2">
                      <div className="w-full rounded-t-md" style={{ height: `${barHeight}px`, background: `linear-gradient(180deg, ${theme.primary}, ${theme.secondary})` }} />
                      <span className="text-[10px]" style={{ color: 'var(--muted)' }}>
                        {dayLabel}
                      </span>
                      <span className="text-[10px] font-bold" style={{ color: '#ffffff' }}>
                        {point.count}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div>
            <h3 className="section-title" style={{ color: 'var(--accent-2)' }}>
              Lead Sources
            </h3>
            <div className="mt-4 space-y-3">
              {(dashStats?.leadSources ?? []).map((src) => {
                const total = (dashStats?.leadSources ?? []).reduce((acc, row) => acc + (row.count ?? 0), 0);
                const pct = total > 0 ? Math.round((src.count / total) * 100) : 0;
                return (
                  <div key={src.source}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span style={{ color: 'var(--text)' }}>{src.source}</span>
                      <span style={{ color: '#ffffff', fontWeight: 700 }}>{pct}%</span>
                    </div>
                    <div className="h-2 rounded bg-[var(--card2)]">
                      <div className="h-2 rounded" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${theme.accent}, ${theme.primary})` }} />
                    </div>
                  </div>
                );
              })}
              {!dashStats?.leadSources?.length ? (
                <p className="text-sm" style={{ color: 'var(--muted)' }}>
                  No source data available yet.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {reportRow !== undefined && reportRow !== null ? (
        <section className="odc-card !p-5" style={{ transform: 'none' }}>
          {reportRow.status === 'generating' ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <span className="odc-float text-2xl">⏳</span>
                <div>
                  <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                    Your business report is being generated...
                  </p>
                  <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                    Please refresh shortly or open the My ODC Report page.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--muted)' }}>
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"
                  style={{ borderColor: `${theme.primary} transparent ${theme.primary} ${theme.primary}` }}
                  aria-hidden
                />
                View when ready
              </div>
            </div>
          ) : null}
          {reportRow.status === 'completed' ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                  ✅ Your Business Report Ready
                </p>
                <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                  {(() => {
                    const parsed = parseReportData(reportRow.report_data);
                    const ovw = (parsed?.executive_summary as { overview?: string } | undefined)?.overview || '';
                    return ovw ? `${ovw.slice(0, 50)}${ovw.length > 50 ? '…' : ''}` : 'Open the full report for insights, agents, and your 3‑month plan.';
                  })()}
                </p>
                <p className="mt-2 text-[11px] font-semibold" style={{ color: 'var(--muted)' }}>
                  🤖 Essential agent picks · 📅 Content plan ready
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link to="/onboarding-report" className="odc-btn-primary !text-xs">
                  View Full Report →
                </Link>
                <button
                  type="button"
                  className="odc-btn-ghost !text-xs"
                  onClick={() => downloadSimpleFromRow(reportRow)}
                >
                  📥 Download
                </button>
              </div>
            </div>
          ) : null}
          {reportRow.status === 'failed' ? (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                  ⚠️ Report generation failed
                </p>
                <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
                  Please try generating it again.
                </p>
              </div>
              <button type="button" className="odc-btn-primary !text-xs" onClick={() => void regenerateReport()}>
                Regenerate Report
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      <div className="grid gap-8 xl:grid-cols-12">
        <div className="space-y-10 xl:col-span-7">
          <section className="odc-card !p-5">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <h2 className="section-title" style={{ color: 'var(--accent-2)' }}>
                AI agents overview
              </h2>
              <button
                type="button"
                className="text-sm font-semibold transition hover:opacity-80"
                style={{ color: theme.primary }}
                onClick={() => nav('/agents')}
              >
                View all agents →
              </button>
            </div>
            <div className="mb-4 flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const active = catTab === c;
                const col = c === 'All' ? theme.primary : colorForCategory(c);
                return (
                  <button
                    key={c}
                    type="button"
                    className={`odc-pill-tab ${active ? 'active' : ''}`}
                    style={
                      active
                        ? {
                            borderColor: col,
                            background: `${col}22`,
                            color: 'var(--text)',
                          }
                        : undefined
                    }
                    onClick={() => setCatTab(c)}
                  >
                    {c === 'All' ? c : c.split(' ')[0]}
                  </button>
                );
              })}
            </div>
            <div className="grid max-h-[520px] grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
              {filteredAgents.map((a) => {
                const on = !!a.is_enabled;
                const locked = !!a.toggle_locked;
                const cat = colorForCategory(a.category);
                return (
                  <div
                    key={a.id}
                    className={`odc-agent-pill flex-col !items-stretch !p-3 sm:flex-row sm:items-center ${on ? 'active' : ''}`}
                    style={{
                      opacity: on ? 1 : 0.65,
                      borderColor: on ? 'rgba(16,185,129,0.45)' : undefined,
                    }}
                  >
                    <div className="flex w-full items-start justify-between gap-2">
                      <span className="text-2xl leading-none odc-float">{a.icon || '🤖'}</span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={on}
                        disabled={locked}
                        className={`odc-toggle shrink-0 ${on ? 'active' : ''} ${locked ? 'cursor-not-allowed opacity-40' : ''}`}
                        onClick={() => {
                          if (locked) return;
                          void toggleAgent(a.id, !on);
                        }}
                      />
                    </div>
                    <span
                      className="mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-black"
                      style={{ background: `${cat}28`, color: cat }}
                    >
                      #{String(a.agent_number).padStart(2, '0')}
                    </span>
                    <p className="truncate text-xs font-semibold" style={{ color: 'var(--text)' }}>
                      {a.name}
                    </p>
                    <p className="text-[10px] font-medium capitalize" style={{ color: 'var(--muted)' }}>
                      {a.label_status || a.status}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="section-title mb-4" style={{ color: 'var(--accent-2)' }}>
              Content calendar · next 7 days
            </h2>
            {calByDay.length === 0 ? (
              <p className="odc-card !p-6 text-sm" style={{ color: 'var(--muted)' }}>
                No posts in this window. Schedule from Content Calendar.
              </p>
            ) : (
              <div className="space-y-4">
                {calByDay.map(({ day, items }) => (
                  <div key={day}>
                    <p
                      className="mb-2 text-xs font-bold uppercase tracking-wider"
                      style={{ color: 'var(--muted)' }}
                    >
                      {day}
                    </p>
                    <div className="space-y-2">
                      {items.map((c) => {
                        const st = c.status.toLowerCase();
                        const col =
                          st === 'published'
                            ? theme.success
                            : st === 'failed'
                              ? theme.danger
                              : st === 'draft'
                                ? '#94A3B8'
                                : '#3B82F6';
                        return (
                          <div
                            key={c.id}
                            className="odc-card flex flex-wrap items-center gap-3 !rounded-xl !p-3 text-sm"
                            style={{ transform: 'none', boxShadow: 'none' }}
                          >
                            <span>{platformIcon(c.platform || '')}</span>
                            <span className="flex-1 truncate font-medium" style={{ color: 'var(--text)' }}>
                              {c.title}
                            </span>
                            <span
                              className="rounded-md px-2 py-0.5 text-[10px] font-bold uppercase"
                              style={{ background: `${col}22`, color: col }}
                            >
                              {c.status}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6 xl:col-span-5">
          <div className="odc-card !p-5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="odc-live-dot" />
                <h3 className="section-title" style={{ color: 'var(--accent-2)' }}>
                  Live activity
                </h3>
              </div>
              <span className="rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: 'var(--muted)', borderColor: 'var(--border)' }}>
                Auto-refresh every 30s
              </span>
            </div>
            <ul
              ref={activityRef}
              className="odc-activity-feed mt-4 space-y-3 text-sm"
            >
              {activity.length === 0 ? (
                <li className="py-8 text-center text-sm italic" style={{ color: 'var(--muted)' }}>
                  No recent activity
                </li>
              ) : (
                activity.map((a, i) => (
                  <li
                    key={`${a.created_at}-${i}`}
                    className="rounded-lg border-l-2 pl-3 transition hover:opacity-90"
                    style={{
                      borderColor: activityDotColor(a.type || 'lead'),
                      color: 'var(--muted)',
                      animation: `slideInRight 0.4s ease ${Math.min(i, 8) * 0.04}s both`,
                    }}
                  >
                    <span style={{ color: 'var(--text)' }}>👤 New lead: {a.title}</span>
                    <div className="text-[10px]" style={{ color: 'var(--muted)' }}>
                      via {a.subtitle}
                    </div>
                    <div className="text-[10px]" style={{ color: 'var(--muted)' }}>
                      {timeAgo(a.created_at)}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>

          <div className="odc-card !p-5">
            <h3 className="section-title" style={{ color: 'var(--accent-2)' }}>
              Social accounts
            </h3>
            <ul className="mt-3 space-y-2 text-sm">
              {social.map((s) => (
                <li
                  key={s.platform}
                  className="flex items-center justify-between rounded-xl border px-3 py-2 capitalize transition hover:border-[var(--border2)]"
                  style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                >
                  <span style={{ color: 'var(--muted)' }}>
                    {platformIcon(s.platform)} {s.platform}
                  </span>
                  <span className="text-right text-xs">
                    {s.is_connected ? (
                      <span className="odc-badge odc-badge-green">Connected</span>
                    ) : (
                      <span className="odc-badge odc-badge-amber">Not connected</span>
                    )}
                    <div style={{ color: 'var(--muted)' }}>{s.followers_count.toLocaleString()} followers</div>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="odc-card !p-5">
            <h3 className="section-title mb-3" style={{ color: 'var(--accent-2)' }}>
              Quick actions
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  ['Add lead', () => nav('/leads')],
                  ['Schedule post', () => nav('/calendar')],
                  ['Generate Content', () => nav('/content-generator')],
                  ['View reports', () => nav('/analytics')],
                  ['Call a lead', () => nav('/calls')],
                ] as [string, () => void][]
              ).map(([label, fn]) => (
                <button key={label} type="button" className="odc-btn-ghost justify-center !py-3 text-xs" onClick={fn}>
                  {label}
                </button>
              ))}
            </div>
            <div className="mt-6 border-t pt-5" style={{ borderColor: 'var(--border)' }}>
              <h4 className="mb-3 text-sm font-bold" style={{ color: 'var(--text)' }}>
                Quick Wins
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  className="odc-btn-ghost justify-center !py-3 text-xs transition hover:border-[#10B981]/55 hover:text-[#6EE7B7]"
                  onClick={() => nav('/agents')}
                >
                  Enable GEO Agent
                </button>
                <button
                  type="button"
                  className="odc-btn-ghost justify-center !py-3 text-xs transition hover:border-[#10B981]/55 hover:text-[#6EE7B7]"
                  onClick={() => nav('/settings')}
                >
                  Setup WhatsApp
                </button>
                <div className="relative">
                  <button
                    type="button"
                    disabled
                    className="odc-btn-ghost w-full cursor-not-allowed justify-center !py-3 text-xs opacity-60"
                  >
                    Build My Website
                  </button>
                  <span
                    className="pointer-events-none absolute -right-1 -top-1 rounded px-1 py-0.5 text-[7px] font-bold uppercase leading-tight"
                    style={{ background: 'rgba(245,158,11,0.25)', color: '#FBBF24' }}
                  >
                    Coming Soon
                  </span>
                </div>
                <button
                  type="button"
                  className="odc-btn-ghost justify-center !py-3 text-xs transition hover:border-[#10B981]/55 hover:text-[#6EE7B7]"
                  onClick={() => nav('/analytics')}
                >
                  View AI Score
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
